import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { MONTHS_FR } from '../utils/dateUtils';
import { useTimesheetEdit } from '../hooks/useTimesheetEdit';
import {
  CheckCircle,
  Clock,
  Calendar,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  FileText,
  Search,
  X,
  AlertCircle,
  Loader2
} from 'lucide-react';

// --- TYPES ---
interface Employee {
  full_name: string;
  role: string;
}

interface WeeklySheet {
  id: string;
  status: string;
  week_number: number;
  year: number;
  total_hours: number;
  pdf_url: string | null;
  override_weekly_hours: number | null;
  employee: Employee;
  // Heures supplémentaires
  regular_hours: number | null;
  overtime_25: number | null;
  overtime_50: number | null;
  sunday_hours: number | null;
}

type FilterMode = 'week' | 'month' | 'year';

// --- CONSTANTES ---

// --- UTILITAIRES DE DATE ---
const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getWeekStartDate = (year: number, week: number): Date => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
};

const getWeekEndDate = (weekStart: Date): Date => {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return end;
};

export default function SecretariatValidate() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { validateSheet } = useTimesheetEdit();
  const { confirm: confirmDialog } = useConfirm();

  // Données
  const [pendingSheets, setPendingSheets] = useState<WeeklySheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState<string | null>(null);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedWeek, setSelectedWeek] = useState(getISOWeekNumber(new Date()));

  // --- Calcul des dates de début/fin selon le mode ---
  const { periodLabel } = useMemo(() => {
    let label: string;

    if (filterMode === 'week') {
      const start = getWeekStartDate(selectedYear, selectedWeek);
      const end = getWeekEndDate(start);
      label = `Semaine ${selectedWeek} (${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })})`;
    } else if (filterMode === 'month') {
      label = `${MONTHS_FR[selectedMonth]} ${selectedYear}`;
    } else {
      label = `Année ${selectedYear}`;
    }

    return { periodLabel: label };
  }, [filterMode, selectedYear, selectedMonth, selectedWeek]);

  // --- Navigation temporelle ---
  const handlePrevious = () => {
    if (filterMode === 'week') {
      if (selectedWeek > 1) {
        setSelectedWeek(selectedWeek - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedWeek(52);
      }
    } else if (filterMode === 'month') {
      if (selectedMonth > 0) {
        setSelectedMonth(selectedMonth - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedMonth(11);
      }
    } else {
      setSelectedYear(selectedYear - 1);
    }
  };

  const handleNext = () => {
    if (filterMode === 'week') {
      if (selectedWeek < 52) {
        setSelectedWeek(selectedWeek + 1);
      } else {
        setSelectedYear(selectedYear + 1);
        setSelectedWeek(1);
      }
    } else if (filterMode === 'month') {
      if (selectedMonth < 11) {
        setSelectedMonth(selectedMonth + 1);
      } else {
        setSelectedYear(selectedYear + 1);
        setSelectedMonth(0);
      }
    } else {
      setSelectedYear(selectedYear + 1);
    }
  };

  const handleToday = () => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth());
    setSelectedWeek(getISOWeekNumber(now));
  };

  // --- Chargement des données ---
  const fetchPending = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('weekly_sheets')
      .select(`
        id, status, week_number, year, total_hours, pdf_url,
        regular_hours, overtime_25, overtime_50, sunday_hours, override_weekly_hours,
        employee:employees!employee_id(full_name, role)
      `)
      .eq('status', 'pending')
      .order('year', { ascending: false })
      .order('week_number', { ascending: false });

    if (error) console.error("Erreur lecture:", error);
    if (data) setPendingSheets(data as unknown as WeeklySheet[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- LOGIQUE DE FILTRAGE ---
  const filteredSheets = useMemo(() => {
    return pendingSheets.filter(sheet => {
      // 1. Recherche par Nom
      const nameMatch = sheet.employee?.full_name.toLowerCase().includes(searchTerm.toLowerCase());

      // 2. Filtre selon le mode
      let periodMatch = true;
      if (filterMode === 'week') {
        periodMatch = sheet.week_number === selectedWeek && sheet.year === selectedYear;
      } else if (filterMode === 'month') {
        // Une semaine appartient au mois si elle commence dans ce mois
        const weekStart = getWeekStartDate(sheet.year, sheet.week_number);
        periodMatch = weekStart.getMonth() === selectedMonth && weekStart.getFullYear() === selectedYear;
      } else {
        periodMatch = sheet.year === selectedYear;
      }

      return nameMatch && periodMatch;
    });
  }, [pendingSheets, searchTerm, filterMode, selectedYear, selectedMonth, selectedWeek]);

  // --- Validation ---
  const handleValidate = async (id: string) => {
    const ok = await confirmDialog({ title: "Valider cette feuille ?", message: "La feuille sera marquée comme validée.", variant: "info", confirmLabel: "Valider" });
    if (!ok) return;

    setValidating(id);
    // `status` est dérivé de `day_statuses` par un trigger DB : écrire seulement
    // status='validated' est ignoré (la feuille reste 'pending'). On passe par
    // validateSheet, qui valide les jours soumis, audite et signale les refus RLS.
    const success = await validateSheet(id, 'secretariat_validate');

    if (success) {
      setPendingSheets((prev) => prev.filter((sheet) => sheet.id !== id));
    }
    setValidating(null);
  };

  const openPdf = (url: string | null) => {
    if (url) window.open(url, '_blank');
    else toast.warning("Aucun fichier PDF n'a été généré pour cette feuille.");
  };

  // Statistiques
  const totalPending = pendingSheets.length;
  const filteredCount = filteredSheets.length;
  const totalHoursPending = filteredSheets.reduce((acc, s) => acc + (s.total_hours || 0), 0);
  const uniqueEmployees = new Set(filteredSheets.map(s => s.employee?.full_name)).size;

  const hasFilters = searchTerm !== '';

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* HEADER */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        padding: '16px 24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button
              onClick={() => navigate('/secretariat')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: '#f1f5f9', border: 'none', color: '#64748b',
                cursor: 'pointer', fontWeight: '500', padding: '8px 12px',
                borderRadius: '8px', fontSize: '0.85rem'
              }}
            >
              <ChevronLeft size={18} /> Retour
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ padding: '10px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '12px', color: 'white' }}>
                <FileCheck size={22} />
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>Validation des Heures</h1>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.8rem' }}>Feuilles en attente de validation</p>
              </div>
            </div>
          </div>

          {/* Badge total */}
          <div style={{
            background: totalPending > 0 ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : '#f0fdf4',
            color: totalPending > 0 ? '#b45309' : '#15803d',
            padding: '8px 16px',
            borderRadius: '20px',
            fontWeight: '700',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {totalPending > 0 ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
            {totalPending} en attente
          </div>
        </div>
      </div>

      <div>

        {/* SECTION FILTRES + STATS */}
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '20px', marginBottom: '24px' }}>

          {/* Carte Filtre de Date */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Période</h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>Filtrer par période</p>
            </div>

            {/* Sélecteur de mode */}
            <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '10px', marginBottom: '12px' }}>
              {(['week', 'month', 'year'] as FilterMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => setFilterMode(mode)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.8rem',
                    background: filterMode === mode ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'transparent',
                    color: filterMode === mode ? 'white' : '#64748b',
                    transition: 'all 0.2s',
                    boxShadow: filterMode === mode ? '0 2px 4px rgba(245, 158, 11, 0.3)' : 'none'
                  }}
                >
                  {mode === 'week' ? 'Semaine' : mode === 'month' ? 'Mois' : 'Année'}
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#f8fafc', padding: '12px', borderRadius: '10px', marginBottom: '12px'
            }}>
              <button
                onClick={handlePrevious}
                style={{
                  padding: '8px', background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Calendar size={16} color="#f59e0b" />
                  <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.9rem' }}>{periodLabel}</span>
                </div>
              </div>
              <button
                onClick={handleNext}
                style={{
                  padding: '8px', background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Sélecteurs détaillés */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px',
                  border: '1px solid #e2e8f0', fontSize: '0.85rem',
                  cursor: 'pointer', background: 'white'
                }}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              {(filterMode === 'month' || filterMode === 'week') && (
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  style={{
                    flex: 2, padding: '10px', borderRadius: '8px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem',
                    cursor: 'pointer', background: 'white'
                  }}
                >
                  {MONTHS_FR.map((m, idx) => (
                    <option key={idx} value={idx}>{m}</option>
                  ))}
                </select>
              )}

              {filterMode === 'week' && (
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem',
                    cursor: 'pointer', background: 'white'
                  }}
                >
                  {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
                    <option key={w} value={w}>S{w}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Bouton Aujourd'hui */}
            <button
              onClick={handleToday}
              style={{
                width: '100%',
                padding: '10px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                color: '#15803d',
                fontWeight: '600',
                fontSize: '0.85rem',
                cursor: 'pointer'
              }}
            >
              Aujourd'hui
            </button>
          </div>

          {/* Cartes Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {/* En attente sur période */}
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '16px', padding: '20px', color: 'white',
              boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                A Valider
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800' }}>
                {loading ? '-' : filteredCount}
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>
                feuille{filteredCount > 1 ? 's' : ''} sur la période
              </div>
            </div>

            {/* Employés concernés */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Employés
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
                {loading ? '-' : uniqueEmployees}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                collaborateur{uniqueEmployees > 1 ? 's' : ''} concerné{uniqueEmployees > 1 ? 's' : ''}
              </div>
            </div>

            {/* Total heures */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              border: '1px solid #dbeafe', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Heures Totales
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
                {loading ? '-' : totalHoursPending.toFixed(0)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                heures à valider
              </div>
            </div>
          </div>
        </div>

        {/* Barre de recherche */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <Search size={20} color="#94a3b8" />
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '0.95rem',
              color: '#1e293b',
              background: 'transparent'
            }}
          />
          {hasFilters && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                background: '#fee2e2', color: '#ef4444', border: 'none',
                padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: '600'
              }}
            >
              <X size={14} /> Effacer
            </button>
          )}
        </div>

        {/* LISTE DES FEUILLES */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
              Feuilles en attente
            </h2>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {filteredCount} résultat{filteredCount > 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Chargement en cours...
              </div>
            </div>
          ) : filteredSheets.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#64748b' }}>
                <CheckCircle size={48} strokeWidth={1.5} color="#10b981" />
                <div>
                  <p style={{ margin: 0, fontWeight: '600', color: '#15803d' }}>Aucune feuille à valider</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                    {totalPending === 0 ? "Tout est à jour !" : "Modifiez vos filtres pour voir d'autres résultats"}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredSheets.map((sheet, idx) => (
                <div
                  key={sheet.id}
                  style={{
                    padding: '20px 24px',
                    borderBottom: idx < filteredSheets.length - 1 ? '1px solid #f1f5f9' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '20px',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  {/* Info employé */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '12px',
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: '700', color: '#b45309', fontSize: '0.9rem'
                    }}>
                      {sheet.employee?.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem', marginBottom: '2px' }}>
                        {sheet.employee?.full_name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.85rem', color: '#64748b', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={14} />
                          S{sheet.week_number} - {sheet.year}
                        </span>
                        <span style={{ color: '#e2e8f0' }}>|</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={14} />
                          <strong style={{ color: '#1e293b' }}>{sheet.total_hours || 0}h</strong>
                        </span>
                        {/* Affichage des heures supplémentaires si présentes */}
                        {((sheet.overtime_25 || 0) > 0 || (sheet.overtime_50 || 0) > 0 || (sheet.sunday_hours || 0) > 0) && (
                          <>
                            <span style={{ color: '#e2e8f0' }}>|</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {(sheet.overtime_25 || 0) > 0 && (
                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>
                                  {sheet.overtime_25?.toFixed(1)}h@25%
                                </span>
                              )}
                              {(sheet.overtime_50 || 0) > 0 && (
                                <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>
                                  {sheet.overtime_50?.toFixed(1)}h@50%
                                </span>
                              )}
                              {(sheet.sunday_hours || 0) > 0 && (
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>
                                  {sheet.sunday_hours?.toFixed(1)}h dim
                                </span>
                              )}
                            </span>
                          </>
                        )}
                        {sheet.override_weekly_hours != null && (
                          <>
                            <span style={{ color: '#e2e8f0' }}>|</span>
                            <span style={{ background: '#fff7ed', color: '#c2410c', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '700', border: '1px solid #fed7aa' }}>
                              Base: {sheet.override_weekly_hours}h
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Badge rôle */}
                  <div style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}>
                    {sheet.employee?.role || 'N/A'}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => openPdf(sheet.pdf_url)}
                      style={{
                        padding: '10px 16px',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        color: '#475569',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#f8fafc';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <FileText size={16} /> PDF
                    </button>
                    <button
                      onClick={() => handleValidate(sheet.id)}
                      disabled={validating === sheet.id}
                      style={{
                        padding: '10px 20px',
                        background: validating === sheet.id ? '#94a3b8' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '10px',
                        cursor: validating === sheet.id ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: validating === sheet.id ? 'none' : '0 4px 6px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.2s'
                      }}
                    >
                      {validating === sheet.id ? (
                        <>
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Validation...
                        </>
                      ) : (
                        <>
                          <FileCheck size={16} /> Valider
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
